import React, { useEffect, useMemo, useState } from 'react';
import { edgesToNodes } from 'utils/storefront/format-resource';
import { decodeId } from 'utils/helpers';
import QuantityInput from 'utils/quantity-input';
import CartItemImage from './cart-item-image';
import CartSubbrand from './cart-subbrand';
import CartProductTitle from './cart-product-title';
import CartItemOverMax from './cart-item-over-max';
import CartItemPrices from './cart-item-prices';
import { useCartContext } from './cart-context';
import CartItemOptions from './cart-item-options';

const CartItem = ({ lineItem }) => {
  const { cart, cartLoading } = useCartContext();
  const product = cart.productsData[lineItem.product_id] || {};
  const subBrand = product?.subBrand?.value;
  const isStorefrontMode = cart.mode === 'storefront';
  const isSaleItem = lineItem.original_line_price > lineItem.line_price;
  const [quantity, setQuantity] = useState(lineItem.quantity);

  useEffect(() => {
    setQuantity(lineItem.quantity);
  }, [lineItem.quantity]);

  const handleRemove = () => {
    let payload = [lineItem.id];

    // If bundle, add children to update request
    if (lineItem.children) {
      payload = lineItem.children.map((child) => child.id);
    }

    return cart.remove(payload);
  };

  const variants = useMemo(() => {
    if (!cart.productsData[lineItem.product_id]?.variants) {
      return null;
    }
    return edgesToNodes(cart.productsData[lineItem.product_id]?.variants, 'variant');
  }, [cart.productsData, lineItem.product_id]);

  const selectedVariant = useMemo(
    () => variants?.find((v) => decodeId(v.id, 'ProductVariant') === lineItem.variant_id),
    [variants, lineItem.variant_id],
  );

  const maxQuantity = useMemo(() => {
    if (lineItem.children) {
      return lineItem.children.reduce((prevQuantity, child) => {
        const childMax =
          (cart.mode === 'storefront' ? child.merchandise?.quantityAvailable : child.quantity_rule?.maxQuantity) ||
          Infinity;
        return childMax < prevQuantity ? childMax : prevQuantity;
      }, Infinity);
    }

    return selectedVariant?.quantityAvailable
      ? selectedVariant.quantityAvailable
      : lineItem.merchandise?.quantityAvailable || lineItem.quantity_rule?.maxQuantity || Infinity;
  }, [selectedVariant, lineItem.merchandise, lineItem.quantity_rule]);

  return (
    <div
      key={lineItem.id}
      className="flex flex-row gap-2"
    >
      <CartItemImage
        cartIsStorefront={isStorefrontMode}
        lineItem={lineItem}
      />

      <div className="flex max-w-[calc(100%-7.5rem)] shrink-0 grow-0 basis-[calc(100%-7.5rem)] flex-col">
        <div className="flex-1">
          <CartSubbrand
            subBrand={subBrand}
            handleRemove={handleRemove}
          />

          <CartProductTitle
            isStorefrontMode={isStorefrontMode}
            lineItem={lineItem}
          />

          <CartItemOptions
            options={lineItem.options_with_values}
            variantId={lineItem.variant_id}
          />

          {maxQuantity < 20 && <CartItemOverMax maxQuantity={maxQuantity - 10} />}
        </div>

        <div className="flex items-end justify-between">
          <CartItemPrices
            item={lineItem}
            cart={cart}
            selectedVariant={selectedVariant}
            isSaleItem={isSaleItem}
          />

          <QuantityInput
            quantity={quantity}
            max={maxQuantity - 10}
            updateCart={cart.update}
            title={window.TMW.Strings.cart.quantityLabel.replace('{product}', lineItem.product_title)}
            setQuantity={setQuantity}
            lineItem={lineItem}
            cartLoading={cartLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default CartItem;
